/**
 * Clearance checking — the design rules Net Scan is supposed to enforce.
 *
 * Every board file carries a `NetScan::` record holding three numbers:
 * line-to-line, line-to-pad and pad-to-pad. They have been parsed and written
 * back faithfully for a long time, but nothing read them: Net Scan reported
 * shorts and unrouted nets and never measured a gap. This is the missing half.
 *
 * The rule is only violated between *different* nets. Copper of one net may
 * touch itself freely — that is what routing is — so the net map from
 * {@link extractNetlist} decides which pairs are worth measuring.
 *
 * **Layers.** Two objects can only clash if they share a layer, with one
 * important exception: a through pad or via sits on the Pad Master and is
 * therefore present on *every* copper layer, so it is checked against all of
 * them. Missing that would pass a via that shorts an inner plane.
 *
 * **Cost.** A real board has a couple of thousand primitives, and the naive
 * pairwise sweep is millions of comparisons. Objects are bucketed into a
 * uniform grid sized to the largest rule, so only neighbours are measured.
 */

import {
  LayerType,
  type CadDocument, type ComponentEntity, type Entity, type LineEntity,
  type NetScanSetting, type PadEntity,
  padIsThrough,
} from '../model.ts';
import { extractNetlist, placeInComponent } from './netlist.ts';
import { padIsRectangular } from '../geom/pad.ts';

/** The original's own defaults, taken from the files it writes. */
export const DEFAULT_NET_SCAN: NetScanSetting = {
  lineToLine: 0.003,
  lineToPad: 0.003,
  padToPad: 0.01,
  ratLine: 0.011,
};

export interface ClearanceViolation {
  kind: 'line-line' | 'line-pad' | 'pad-pad';
  /** The rule that was broken, in inches. */
  required: number;
  /** The gap actually measured, edge to edge. Negative means overlapping. */
  actual: number;
  nets: [string, string];
  at: { x: number; y: number };
  entityIds: [number, number];
  layer: number;
}

/** A primitive flattened to world coordinates, with the net it belongs to. */
interface Item {
  id: number;
  net: string;
  /** Layers this occupies. A through pad occupies every copper layer. */
  layers: number[];
  kind: 'line' | 'pad';
  /** Segment ends (a pad uses its centre for both). */
  x1: number; y1: number; x2: number; y2: number;
  /** Half the stroke width for a line; 0 for a pad, whose size is its extent. */
  half: number;
  /** Pad geometry, when this is a pad. */
  pad?: { sx: number; sy: number; rot: number; rect: boolean };
  /** Bounding box, for bucketing. */
  bx1: number; by1: number; bx2: number; by2: number;
}

export function clearanceViolations(
  doc: CadDocument, rules: NetScanSetting = doc.netScan ?? DEFAULT_NET_SCAN,
): ClearanceViolation[] {
  if (doc.kind !== 'pcb') return [];

  const copper = doc.layers
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.type === LayerType.Copper || l.type === LayerType.Plane)
    .map(({ i }) => i);
  if (copper.length === 0) return [];

  // Which net each primitive belongs to.
  //
  // A primitive's own `Signal:` field is the authority — CIRCAD writes the net
  // onto the copper itself — and the netlist's union-find is the fallback for
  // anything unlabelled. Relying on the netlist alone reported a track against
  // its own pad as a violation, because `entityIds` does not claim every
  // primitive that carries a signal.
  const netOf = new Map<number, string>();
  for (const net of extractNetlist(doc).nets) {
    for (const id of net.entityIds) netOf.set(id, net.name);
  }

  const items = collect(doc, copper, netOf);
  if (items.length < 2) return [];

  const worst = Math.max(rules.lineToLine, rules.lineToPad, rules.padToPad);
  return sweep(items, rules, worst);
}

/** Flatten the document — including component children — into world-space items. */
function collect(
  doc: CadDocument, copper: number[], netOf: Map<number, string>,
): Item[] {
  const out: Item[] = [];
  const padMaster = doc.layers.findIndex((l) => l.name === 'Pad Master');

  /**
   * The copper layers a primitive occupies, or null if it carries no copper.
   *
   * The Pad Master reaches every copper layer, but only for **pads**: a line
   * or arc drawn there is the board outline, not copper, and measuring the
   * outline against every track on every layer invents violations along the
   * whole edge of the board.
   *
   * And only a *through* pad reaches them. A surface pad is on one side and
   * nowhere else, so it cannot clash with copper on the other — there is a
   * board in between. Giving every pad every layer measured the top pads of
   * fine-pitch parts against the tracks running underneath them and called
   * each one too close: on the STM32 sample that was most of the report.
   */
  const layersFor = (layer: number, through: boolean | null): number[] | null => {
    if (through === null) return copper.includes(layer) ? [layer] : null;
    if (layer === padMaster) return copper;
    if (!copper.includes(layer)) return null;
    return through ? copper : [layer];
  };

  /**
   * The net a primitive belongs to: its own `Signal:` first, then the netlist.
   *
   * Unlabelled copper falls back to a name unique to the entity, so a stray
   * fragment is still measured against everything around it rather than being
   * quietly excused as "same net".
   */
  const netFor = (e: Entity): string => {
    const own = 'signal' in e ? (e as { signal?: string }).signal : undefined;
    return own || netOf.get(e.id) || `?${e.id}`;
  };

  const add = (e: Entity, owner: ComponentEntity | null): void => {
    if (e.kind === 'line') {
      const layers = layersFor(e.layer, null);
      if (!layers) return;
      const a = owner ? placeInComponent({ x: e.x1, y: e.y1 }, owner) : { x: e.x1, y: e.y1 };
      const b = owner ? placeInComponent({ x: e.x2, y: e.y2 }, owner) : { x: e.x2, y: e.y2 };
      const half = (e as LineEntity).width / 2;
      out.push({
        id: e.id, net: netFor(e), layers, kind: 'line',
        x1: a.x, y1: a.y, x2: b.x, y2: b.y, half,
        bx1: Math.min(a.x, b.x) - half, by1: Math.min(a.y, b.y) - half,
        bx2: Math.max(a.x, b.x) + half, by2: Math.max(a.y, b.y) + half,
      });
      return;
    }
    if (e.kind === 'pad') {
      const p = e as PadEntity;
      // Schematic pins produce no copper.
      if (p.type === 'T0' || p.type === 'S3') return;
      const layers = layersFor(p.layer, padIsThrough(p.type));
      if (!layers) return;
      const w = owner ? placeInComponent({ x: p.x, y: p.y }, owner) : { x: p.x, y: p.y };
      const rot = (p.rotation ?? 0) + (owner?.rotation ?? 0);
      const reach = Math.hypot(p.sizeX, p.sizeY) / 2;
      out.push({
        id: p.id, net: netFor(p), layers, kind: 'pad',
        x1: w.x, y1: w.y, x2: w.x, y2: w.y, half: 0,
        pad: { sx: p.sizeX, sy: p.sizeY, rot, rect: padIsRectangular(p) },
        bx1: w.x - reach, by1: w.y - reach, bx2: w.x + reach, by2: w.y + reach,
      });
      return;
    }
    if (e.kind === 'component') {
      for (const c of e.children) add(c, e);
    }
  };

  for (const e of doc.entities) add(e, null);
  return out;
}

/**
 * Measure every neighbouring pair.
 *
 * The grid cell is the largest rule plus the largest object, so two objects
 * that could possibly be too close always share or neighbour a cell.
 */
function sweep(items: Item[], rules: NetScanSetting, worst: number): ClearanceViolation[] {
  const pairs = candidatePairs(items, worst);

  // --- Pass one: copper that touches is one conductor.
  //
  // A track is drawn as a chain of Line records, and adjoining segments
  // overlap at the corner. Measured naively that is a negative gap between two
  // different "nets", and a board reports hundreds of violations at its own
  // corners. Touching pieces are therefore merged first.
  //
  // The merge only runs when at least one side is unlabelled. Two *named*
  // nets that touch are a genuine short, and must stay visible rather than
  // being quietly fused into one net.
  const group = new DisjointSet(items.length);
  for (const [i, j] of pairs) {
    const a = items[i]!;
    const b = items[j]!;
    if (shared(a.layers, b.layers) === null) continue;
    const anon = a.net.startsWith('?') || b.net.startsWith('?');
    if (!anon) continue;
    if (separation(a, b) <= 0) group.union(i, j);
  }

  // Each merged group takes the first real signal name in it.
  const nameOf = new Map<number, string>();
  for (let i = 0; i < items.length; i++) {
    const root = group.find(i);
    const own = items[i]!.net;
    if (!own.startsWith('?') && !nameOf.has(root)) nameOf.set(root, own);
  }
  const netAt = (i: number): string => {
    const root = group.find(i);
    return nameOf.get(root) ?? `?${root}`;
  };

  // --- Pass two: measure what is left.
  const out: ClearanceViolation[] = [];
  for (const [i, j] of pairs) {
    const a = items[i]!;
    const b = items[j]!;
    const na = netAt(i);
    const nb = netAt(j);
    if (na === nb) continue;                     // one conductor may touch itself

    const layer = shared(a.layers, b.layers);
    if (layer === null) continue;

    const required = a.kind === 'line' && b.kind === 'line' ? rules.lineToLine
      : a.kind === 'pad' && b.kind === 'pad' ? rules.padToPad
        : rules.lineToPad;
    if (required <= 0) continue;

    const gap = separation(a, b);
    if (gap >= required) continue;

    out.push({
      kind: a.kind === 'line' && b.kind === 'line' ? 'line-line'
        : a.kind === 'pad' && b.kind === 'pad' ? 'pad-pad' : 'line-pad',
      required,
      actual: gap,
      nets: [na, nb],
      at: midpoint(a, b),
      entityIds: [a.id, b.id],
      layer,
    });
  }

  // Worst first: the tightest gaps are the ones that will actually short.
  out.sort((p, q) => (p.actual - p.required) - (q.actual - q.required));
  return out;
}

/**
 * Index pairs close enough to be worth measuring.
 *
 * Each object is filed into *every* grid cell its inflated box covers, rather
 * than just the cell its corner falls in. Sizing the grid to the largest
 * object instead — a board-length track is one object — collapsed every item
 * into a single bucket and turned the sweep back into the full pairwise
 * comparison it was meant to avoid.
 */
function candidatePairs(items: Item[], worst: number): [number, number][] {
  const CELL = 0.2;                              // inches; a few pad pitches
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    const x0 = Math.floor((it.bx1 - worst) / CELL);
    const x1 = Math.floor((it.bx2 + worst) / CELL);
    const y0 = Math.floor((it.by1 - worst) / CELL);
    const y1 = Math.floor((it.by2 + worst) / CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = `${cx},${cy}`;
        const list = buckets.get(k);
        if (list) list.push(i);
        else buckets.set(k, [i]);
      }
    }
  }

  const seen = new Set<number>();
  const out: [number, number][] = [];
  for (const list of buckets.values()) {
    for (let m = 0; m < list.length; m++) {
      for (let n = m + 1; n < list.length; n++) {
        const i = list[m]!;
        const j = list[n]!;
        const a = i < j ? i : j;
        const b = i < j ? j : i;
        const key = a * items.length + b;
        if (seen.has(key)) continue;
        seen.add(key);
        // Cheap box rejection before anything expensive.
        const p = items[a]!;
        const q = items[b]!;
        if (p.bx1 - q.bx2 > worst || q.bx1 - p.bx2 > worst
          || p.by1 - q.by2 > worst || q.by1 - p.by2 > worst) continue;
        out.push([a, b]);
      }
    }
  }
  return out;
}

/** Minimal union-find, for merging copper that touches. */
class DisjointSet {
  private readonly parent: number[];

  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }

  find(i: number): number {
    let root = i;
    while (this.parent[root] !== root) root = this.parent[root]!;
    while (this.parent[i] !== root) {
      const next = this.parent[i]!;
      this.parent[i] = root;
      i = next;
    }
    return root;
  }

  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

function shared(a: number[], b: number[]): number | null {
  for (const l of a) if (b.includes(l)) return l;
  return null;
}

function midpoint(a: Item, b: Item): { x: number; y: number } {
  return { x: (a.x1 + b.x1) / 2, y: (a.y1 + b.y1) / 2 };
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Edge-to-edge separation. Negative when the two overlap. */
function separation(a: Item, b: Item): number {
  if (a.kind === 'line' && b.kind === 'line') {
    return segSegDistance(a, b) - a.half - b.half;
  }
  if (a.kind === 'pad' && b.kind === 'pad') {
    return padPadDistance(a, b);
  }
  const line = a.kind === 'line' ? a : b;
  const pad = a.kind === 'pad' ? a : b;
  return segPadDistance(line, pad) - line.half;
}

function segSegDistance(a: Item, b: Item): number {
  return segSeg(a.x1, a.y1, a.x2, a.y2, b.x1, b.y1, b.x2, b.y2);
}

/**
 * Distance from a segment to a pad.
 *
 * A rectangular pad is measured in its own frame: rotating the segment into
 * pad-local coordinates turns the problem into a segment against an
 * axis-aligned box, which is exact rather than an approximation by radius.
 */
function segPadDistance(line: Item, pad: Item): number {
  const p = pad.pad!;
  if (!p.rect) {
    // Round or oval: nearest approach to the centre, less the radius. An oval
    // is treated by its smaller half-axis, which is the conservative reading.
    const d = pointSeg(pad.x1, pad.y1, line.x1, line.y1, line.x2, line.y2);
    return d - Math.min(p.sx, p.sy) / 2;
  }
  const [ax, ay] = toLocal(line.x1, line.y1, pad.x1, pad.y1, p.rot);
  const [bx, by] = toLocal(line.x2, line.y2, pad.x1, pad.y1, p.rot);
  return segBoxDistance(ax, ay, bx, by, p.sx / 2, p.sy / 2);
}

function padPadDistance(a: Item, b: Item): number {
  const pa = a.pad!;
  const pb = b.pad!;
  // Two round pads: centres less both radii.
  if (!pa.rect && !pb.rect) {
    return Math.hypot(a.x1 - b.x1, a.y1 - b.y1)
      - Math.min(pa.sx, pa.sy) / 2 - Math.min(pb.sx, pb.sy) / 2;
  }
  // Otherwise measure the round one (or the other's centre) against the box.
  const [rect, other, orad] = pa.rect
    ? [a, b, pb.rect ? 0 : Math.min(pb.sx, pb.sy) / 2] as const
    : [b, a, Math.min(pa.sx, pa.sy) / 2] as const;
  const rp = rect.pad!;
  const [ox, oy] = toLocal(other.x1, other.y1, rect.x1, rect.y1, rp.rot);
  const box = pointBoxDistance(ox, oy, rp.sx / 2, rp.sy / 2);
  if (orad > 0) return box - orad;
  // Box against box: approximate the second by its half-diagonal, which never
  // reports a violation that is not there.
  const op = other.pad!;
  return box - Math.min(op.sx, op.sy) / 2;
}

/** Rotate a world point into a pad's own frame. */
function toLocal(x: number, y: number, cx: number, cy: number, rot: number): [number, number] {
  const r = (-rot * Math.PI) / 180;
  const dx = x - cx;
  const dy = y - cy;
  return [dx * Math.cos(r) - dy * Math.sin(r), dx * Math.sin(r) + dy * Math.cos(r)];
}

function pointBoxDistance(x: number, y: number, hx: number, hy: number): number {
  const dx = Math.abs(x) - hx;
  const dy = Math.abs(y) - hy;
  if (dx <= 0 && dy <= 0) return -Math.min(-dx, -dy); // inside
  return Math.hypot(Math.max(dx, 0), Math.max(dy, 0));
}

/** Segment against an axis-aligned box centred on the origin. */
function segBoxDistance(
  ax: number, ay: number, bx: number, by: number, hx: number, hy: number,
): number {
  // Sample the segment against the box. The box's own edges are the other half
  // of the problem, so both are measured and the smaller wins.
  let best = Math.min(pointBoxDistance(ax, ay, hx, hy), pointBoxDistance(bx, by, hx, hy));
  const corners: [number, number][] = [[-hx, -hy], [hx, -hy], [hx, hy], [-hx, hy]];
  for (let i = 0; i < 4; i++) {
    const c = corners[i]!;
    const d = corners[(i + 1) % 4]!;
    best = Math.min(best, segSeg(ax, ay, bx, by, c[0], c[1], d[0], d[1]));
  }
  return best;
}

function pointSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len = dx * dx + dy * dy;
  if (len === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function segSeg(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): number {
  // Crossing segments are at zero distance.
  const d1 = cross(cx, cy, dx, dy, ax, ay);
  const d2 = cross(cx, cy, dx, dy, bx, by);
  const d3 = cross(ax, ay, bx, by, cx, cy);
  const d4 = cross(ax, ay, bx, by, dx, dy);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
    && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return 0;

  return Math.min(
    pointSeg(ax, ay, cx, cy, dx, dy),
    pointSeg(bx, by, cx, cy, dx, dy),
    pointSeg(cx, cy, ax, ay, bx, by),
    pointSeg(dx, dy, ax, ay, bx, by),
  );
}

function cross(ax: number, ay: number, bx: number, by: number, px: number, py: number): number {
  return (bx - ax) * (py - ay) - (by - ay) * (px - ax);
}
